import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download, FileText, Play, Search } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { ScrollShadow } from "../components/ui/ScrollShadow";
import { ErrorTile, StatusChip, Td, Th, fmtDateTime, fmtDuration, todayInTz, tzLabel } from "../components/reports/ReportBits";
import { numbersApi, type NumberListItem } from "../api/numbersApi";
import { reportsApi, type CdrItem, type CdrRangeReport } from "../api/reportsApi";
import { formatPhoneNumber } from "../lib/phone";
import { downloadExport, type ExportFormat } from "../lib/dataSheetTransfer";

const filterInput: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontFamily: "inherit",
  fontSize: "0.9rem",
  background: "white",
};

/** "yyyy-MM-dd" + days → "yyyy-MM-dd" (calendar arithmetic only). */
function addDays(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
}

/** The filters a result set was loaded with; paging and search re-query with these. */
type Query = { from: string; to: string; dnisId: number | null; hideUnanswered: boolean };

/**
 * CDR report: every call (inbound and outbound) between a start and end
 * moment, optionally for one number. Times are entered and shown in the
 * customer's report time zone. Nothing loads until "Go"; the result is
 * paged server-side (250 per page), searchable across the whole set and
 * exportable to Excel/CSV.
 */
export function CdrReportPage() {
  const today = todayInTz();
  const [from, setFrom] = useState(`${today}T00:00`);
  const [to, setTo] = useState(`${addDays(today, 1)}T00:00`);
  const [dnisId, setDnisId] = useState<number | null>(null);
  const [hideUnanswered, setHideUnanswered] = useState(true);
  const [numbers, setNumbers] = useState<NumberListItem[]>([]);

  const [query, setQuery] = useState<Query | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CdrRangeReport | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);

  useEffect(() => {
    let active = true;
    numbersApi.list().then((r) => active && setNumbers(r.items ?? [])).catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // Load whenever the applied query, search or page changes (never before Go).
  useEffect(() => {
    if (!query) return;
    let active = true;
    setLoading(true);
    setError(null);
    reportsApi
      .cdrRange(query.from, query.to, query.dnisId, search, page, query.hideUnanswered)
      .then((r) => active && setResult(r))
      .catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load calls"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [query, search, page]);

  const numberLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of numbers) m.set(n.number.replace(/\D/g, ""), formatPhoneNumber(n.number, n.countryCode, n.areaCode) || n.number);
    return m;
  }, [numbers]);
  const labelFor = (dnis: string) => numberLabel.get((dnis ?? "").replace(/\D/g, "")) ?? dnis ?? "—";

  const rangeError = from && to && to <= from ? "End must be after start." : null;

  const go = () => {
    if (rangeError || !from || !to) return;
    setSearchInput("");
    setSearch("");
    setPage(1);
    setResult(null);
    setQuery({ from, to, dnisId, hideUnanswered });
  };

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const exportAll = async (format: ExportFormat) => {
    if (!query) return;
    setExporting(format);
    setError(null);
    try {
      const all = await reportsApi.cdrRangeExport(query.from, query.to, query.dnisId, search, query.hideUnanswered);
      const headers = [`Start (${tzLabel()})`, "Direction", "Calling number", "Called number", "Number", "Ring time (s)", "Talk time (s)", "Status", "Hangup cause", "Call id"];
      const rows = all.items.map((c) => [
        fmtDateTime(c.inviteUtc),
        c.direction === "OUT" ? "Outbound" : "Inbound",
        c.callingNumber,
        c.calledNumber,
        labelFor(c.dnisPresentation),
        c.ringSeconds != null ? String(c.ringSeconds) : "",
        c.acceptUtc != null ? String(c.seconds ?? 0) : "",
        c.acceptUtc != null ? "Answered" : "Not answered",
        c.hangupCauseNormalised,
        c.callId,
      ]);
      downloadExport(format, `cdr-${query.from.replace(/[:T]/g, "-")}-${query.to.replace(/[:T]/g, "-")}`, headers, rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(null);
    }
  };

  const total = result?.total ?? 0;
  const pageSize = result?.pageSize ?? 250;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <FileText size={22} aria-hidden="true" /> CDR
          </span>
        }
      />
      <div className="dashboard">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            go();
          }}
          style={{ display: "flex", alignItems: "flex-end", gap: 14, flexWrap: "wrap", fontSize: "0.9rem", margin: "4px 0 12px" }}
        >
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ color: "var(--portal-muted)" }}>Start ({tzLabel()})</span>
            <input type="datetime-local" className="portal-input-inline" style={filterInput} value={from} onChange={(e) => setFrom(e.target.value)} required />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ color: "var(--portal-muted)" }}>End ({tzLabel()})</span>
            <input type="datetime-local" className="portal-input-inline" style={filterInput} value={to} min={from} onChange={(e) => setTo(e.target.value)} required />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ color: "var(--portal-muted)" }}>Number</span>
            <select className="portal-input-inline" value={dnisId ?? ""} onChange={(e) => setDnisId(e.target.value ? Number(e.target.value) : null)} style={{ ...filterInput, minWidth: 180 }}>
              <option value="">All numbers</option>
              {numbers.map((n) => (
                <option key={n.dnisId} value={n.dnisId}>{formatPhoneNumber(n.number, n.countryCode, n.areaCode) || n.number}</option>
              ))}
            </select>
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingBottom: 8 }}>
            <input type="checkbox" checked={hideUnanswered} onChange={(e) => setHideUnanswered(e.target.checked)} />
            Hide unanswered
          </label>
          <button type="submit" className="btn btn-primary" disabled={loading || !!rangeError} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Play size={14} /> {loading && !result ? "Loading…" : "Go"}
          </button>
        </form>

        {rangeError && <ErrorTile message={rangeError} />}
        {error && <ErrorTile message={error} />}

        {!query && !error && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Choose a period and a number, then press Go to load the calls.
          </div>
        )}

        {query && (
          <>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                applySearch();
              }}
              style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: "0.9rem", margin: "0 0 12px" }}
            >
              <input
                type="search"
                className="portal-input-inline"
                style={{ ...filterInput, minWidth: 260 }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search number, called number, hangup cause…"
                aria-label="Search within the result"
              />
              <button type="submit" className="btn btn-secondary" disabled={loading} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Search size={14} /> Search
              </button>
              {search && (
                <button type="button" className="btn btn-secondary" onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}>
                  Clear
                </button>
              )}
              <span style={{ color: "var(--portal-muted)" }}>
                {result ? `${total} ${total === 1 ? "call" : "calls"}${search ? ` matching "${search}"` : ""}` : "Loading…"}
              </span>
              <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => void exportAll("xlsx")} disabled={!result || total === 0 || exporting != null} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Download size={14} /> {exporting === "xlsx" ? "Exporting…" : "Excel"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => void exportAll("csv")} disabled={!result || total === 0 || exporting != null} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Download size={14} /> {exporting === "csv" ? "Exporting…" : "CSV"}
                </button>
              </span>
            </form>

            <section className="tile" style={{ padding: 0, opacity: loading ? 0.6 : 1 }}>
              <ScrollShadow>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.9rem" }}>
                  <thead>
                    <tr>
                      <Th>Start ({tzLabel()})</Th>
                      <Th>Direction</Th>
                      <Th>Calling number</Th>
                      <Th>Called number</Th>
                      <Th>Number</Th>
                      <Th align="right">Ring time</Th>
                      <Th align="right">Talk time</Th>
                      <Th>Status</Th>
                      <Th>Hangup cause</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {result && result.items.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: 16, color: "var(--portal-muted)", textAlign: "center" }}>{search ? "No calls match the search." : "No calls in this period."}</td></tr>
                    )}
                    {!result && (
                      <tr><td colSpan={9} style={{ padding: 16, color: "var(--portal-muted)", textAlign: "center" }}>Loading calls…</td></tr>
                    )}
                    {result?.items.map((c: CdrItem) => {
                      const answered = c.acceptUtc != null;
                      return (
                        <tr key={c.cdrId} style={c.isCredit ? { color: "var(--portal-muted)" } : undefined}>
                          <Td>{fmtDateTime(c.inviteUtc)}</Td>
                          <Td>{c.direction === "OUT" ? "Outbound" : "Inbound"}</Td>
                          <Td>
                            {c.callingNumber || "—"}
                            {c.isCredit && (
                              <span
                                title={`Continuation of call ${c.originalCallId ?? ""} after a credit top-up`}
                                style={{ marginLeft: 8, padding: "1px 7px", borderRadius: 999, border: "1px solid var(--portal-border)", background: "var(--portal-surface)", fontSize: "0.7rem", fontWeight: 600, verticalAlign: "middle" }}
                              >
                                Credit
                              </span>
                            )}
                          </Td>
                          <Td>{c.calledNumber || "—"}</Td>
                          <Td>{labelFor(c.dnisPresentation)}</Td>
                          <Td align="right">{c.ringSeconds != null ? fmtDuration(c.ringSeconds) : "—"}</Td>
                          <Td align="right">{answered ? fmtDuration(c.seconds ?? 0) : "—"}</Td>
                          <Td><StatusChip ok={answered} label={answered ? "Answered" : "Not answered"} /></Td>
                          <Td>
                            <code title={c.hangupCause !== c.hangupCauseNormalised ? `raw: ${c.hangupCause || "(empty)"}` : undefined} style={{ fontSize: "0.8rem" }}>
                              {c.hangupCauseNormalised || "—"}
                            </code>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollShadow>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", borderTop: "1px solid var(--portal-border)", fontSize: "0.85rem", color: "var(--portal-muted)" }}>
                <span>{total === 0 ? "" : `${first}–${last} of ${total}`}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}>
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <span>Page {page} of {pageCount}</span>
                  <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount || loading} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}>
                    Next <ChevronRight size={14} />
                  </button>
                </span>
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}
